'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Shield, Star, Crown, Check, ArrowRight, Lock, Zap, Clock, Users, Gift, ChevronDown } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface Tier {
  id: 'standard' | 'gold' | 'vip';
  name: string;
  tagline: string;
  icon: React.ElementType;
  color: string;
  borderColor: string;
  bgColor: string;
  iconColor: string;
  badgeBg: string;
  entry: string;
  annualCommit: string;
  benefits: Array<{ text: string; highlight?: boolean }>;
  perks: string[];
  priority: string;
  dedicated: boolean;
  revisions: string;
  responseTime: string;
  popular?: boolean;
}

const tiers: Tier[] = [
  {
    id: 'standard',
    name: 'Standard',
    tagline: 'For brands getting started with premium content',
    icon: Shield,
    color: 'text-studio-300',
    borderColor: 'border-studio-600/50',
    bgColor: 'bg-studio-900',
    iconColor: 'text-studio-300',
    badgeBg: 'bg-studio-700/60',
    entry: 'Per-project basis',
    annualCommit: 'No minimum',
    benefits: [
      { text: 'Access to all 4 service lines' },
      { text: '2 revision rounds per project' },
      { text: '48-hour response SLA' },
      { text: 'Standard delivery timeline' },
      { text: 'Project portal access' },
      { text: 'End-of-project review call' },
    ],
    perks: ['Project brief template', 'Asset delivery via portal'],
    priority: 'Standard queue',
    dedicated: false,
    revisions: '2 rounds',
    responseTime: '48 hours',
  },
  {
    id: 'gold',
    name: 'Gold',
    tagline: 'For growing brands that run content consistently',
    icon: Star,
    color: 'text-gold-400',
    borderColor: 'border-gold-500/40',
    bgColor: 'bg-studio-900',
    iconColor: 'text-gold-400',
    badgeBg: 'bg-gold-500/10',
    entry: '3+ projects / year',
    annualCommit: '$15,000+ annually',
    benefits: [
      { text: 'Everything in Standard' },
      { text: '4 revision rounds per project', highlight: true },
      { text: '12-hour response SLA', highlight: true },
      { text: 'Expedited production scheduling' },
      { text: 'Dedicated project manager' },
      { text: 'Quarterly strategy session' },
      { text: 'Early access to AI pipeline features', highlight: true },
      { text: 'Co-branded case study option' },
    ],
    perks: ['Priority scheduling', 'Monthly check-in call', 'Exclusive rate card', 'BTS content package'],
    priority: 'Priority queue',
    dedicated: true,
    revisions: '4 rounds',
    responseTime: '12 hours',
    popular: true,
  },
  {
    id: 'vip',
    name: 'VIP',
    tagline: 'For flagship brands and long-term creative partners',
    icon: Crown,
    color: 'text-white',
    borderColor: 'border-white/20',
    bgColor: 'bg-studio-900',
    iconColor: 'text-white',
    badgeBg: 'bg-white/8',
    entry: 'By invitation only',
    annualCommit: '$50,000+ annually',
    benefits: [
      { text: 'Everything in Gold' },
      { text: 'Unlimited revisions', highlight: true },
      { text: '4-hour response SLA — 24/7', highlight: true },
      { text: 'Dedicated creative director', highlight: true },
      { text: 'On-call production team' },
      { text: 'Monthly creative retainer' },
      { text: 'First access to new service launches', highlight: true },
      { text: 'Co-produced brand content', highlight: true },
      { text: 'White-glove onboarding & offboarding' },
      { text: 'Annual in-person strategy session' },
    ],
    perks: ['24/7 creative hotline', 'Dedicated Slack channel', 'Custom SLA agreement', 'Invite to NASARC annual event'],
    priority: 'Immediate',
    dedicated: true,
    revisions: 'Unlimited',
    responseTime: '4 hours',
  },
];

function TierCard({ tier, index }: { tier: Tier; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const Icon = tier.icon;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const isVip = tier.id === 'vip';
  const isGold = tier.id === 'gold';

  return (
    <div
      ref={ref}
      className={`relative rounded-2xl border overflow-hidden transition-all duration-500 card-hover ${tier.bgColor} ${tier.borderColor} ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${isGold ? 'glow-gold' : ''}`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      {/* VIP gradient background */}
      {isVip && (
        <div className="absolute inset-0 bg-gradient-to-br from-studio-800/60 via-studio-900 to-studio-900" />
      )}

      {/* Gold gradient background */}
      {isGold && (
        <div className="absolute inset-0 bg-gradient-to-br from-gold-500/5 via-transparent to-transparent" />
      )}

      {/* Popular badge */}
      {tier.popular && (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-0 z-10">
          <div className="gradient-gold text-studio-950 text-[10px] font-bold tracking-[0.15em] uppercase px-4 py-1 rounded-b-lg">
            Most Popular
          </div>
        </div>
      )}

      <div className="relative p-6 xl:p-8">
        {/* Tier header */}
        <div className="flex items-center gap-3 mb-5 mt-3">
          <div className={`w-10 h-10 rounded-xl border ${tier.badgeBg} ${tier.borderColor} flex items-center justify-center`}>
            <Icon size={20} className={tier.iconColor} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className={`font-display font-bold text-xl ${tier.color}`}>{tier.name}</h3>
              {isVip && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-white/10 rounded-full">
                  <Lock size={9} className="text-white/60" />
                  <span className="text-white/60 text-[9px] font-medium">Invite Only</span>
                </span>
              )}
            </div>
            <p className="text-studio-500 text-xs mt-0.5">{tier.tagline}</p>
          </div>
        </div>

        {/* Entry */}
        <div className="mb-6 p-3 rounded-xl bg-studio-950/60 border border-studio-700/30">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-studio-500 text-xs">Entry Requirement</span>
            <span className={`text-sm font-semibold ${tier.color}`}>{tier.entry}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-studio-500 text-xs">Annual Commitment</span>
            <span className="text-studio-300 text-sm font-medium">{tier.annualCommit}</span>
          </div>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { icon: Clock, label: 'Response', value: tier.responseTime },
            { icon: Zap, label: 'Revisions', value: tier.revisions },
            { icon: Users, label: 'Manager', value: tier.dedicated ? 'Dedicated' : 'Shared' },
          ].map((stat) => (
            <div key={stat.label} className="flex flex-col items-center text-center p-2.5 bg-studio-950/40 rounded-xl border border-studio-700/20">
              <stat.icon size={12} className="text-studio-500 mb-1.5" />
              <span className={`text-xs font-semibold ${tier.color} leading-tight`}>{stat.value}</span>
              <span className="text-studio-600 text-[10px] mt-0.5">{stat.label}</span>
            </div>
          ))}
        </div>

        {/* Benefits */}
        <div className="mb-5">
          <p className="text-studio-500 text-[10px] font-semibold tracking-[0.12em] uppercase mb-3">Included Benefits</p>
          <div className="space-y-2">
            {tier.benefits.slice(0, expanded ? tier.benefits.length : 5).map((benefit) => (
              <div key={benefit.text} className="flex items-start gap-2">
                <Check
                  size={12}
                  className={`mt-0.5 shrink-0 ${benefit.highlight ? tier.iconColor : 'text-studio-500'}`}
                />
                <span className={`text-sm leading-snug ${benefit.highlight ? 'text-studio-100 font-medium' : 'text-studio-400'}`}>
                  {benefit.text}
                </span>
              </div>
            ))}
          </div>
          {tier.benefits.length > 5 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-3 flex items-center gap-1 text-xs font-medium text-studio-400 hover:text-white transition-colors"
            >
              {expanded ? 'Show less' : `+${tier.benefits.length - 5} more benefits`}
              <ChevronDown size={12} className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>

        {/* Perks */}
        <div className="mb-6">
          <p className="text-studio-500 text-[10px] font-semibold tracking-[0.12em] uppercase mb-2.5">Exclusive Perks</p>
          <div className="flex flex-wrap gap-1.5">
            {tier.perks.map((perk) => (
              <span key={perk} className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-[11px] font-medium ${tier.badgeBg} ${tier.borderColor} ${tier.color}`}>
                <Gift size={9} />
                {perk}
              </span>
            ))}
          </div>
        </div>

        {/* CTA */}
        {isVip ? (
          <button
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="w-full py-3.5 bg-white/8 border border-white/20 text-white text-sm font-semibold rounded-xl hover:bg-white/15 transition-all duration-200 active:scale-95 flex items-center justify-center gap-2"
          >
            <Lock size={13} />
            Request VIP Consideration
          </button>
        ) : isGold ? (
          <button
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="w-full py-3.5 gradient-gold text-studio-950 text-sm font-bold rounded-xl hover:opacity-90 transition-all duration-200 active:scale-95 flex items-center justify-center gap-2"
          >
            Upgrade to Gold
            <ArrowRight size={14} />
          </button>
        ) : (
          <button
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="w-full py-3.5 bg-studio-800/60 border border-studio-600/50 text-studio-200 text-sm font-medium rounded-xl hover:text-white hover:border-studio-500/60 hover:bg-studio-700/60 transition-all duration-200 active:scale-95 flex items-center justify-center gap-2"
          >
            Get Started
            <ArrowRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function TierSystem() {
  return (
    <section id="tiers" className="py-24 xl:py-32 scroll-mt-20 relative" aria-label="Client tier system">
      {/* Background treatment */}
      <div className="absolute inset-0 bg-gradient-to-b from-studio-950 via-studio-900/30 to-studio-950" />

      <div className="relative max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-12 2xl:px-16">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-gold-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3">
            Client Tiers
          </p>
          <h2 className="font-display font-bold text-4xl md:text-5xl xl:text-6xl text-white leading-tight mb-5">
            The More You Build,
            <br />
            <span className="gradient-text-gold">The More You Get</span>
          </h2>
          <p className="text-studio-400 text-base leading-relaxed">
            NASARC's tier system rewards long-term creative partnerships. As your commitment grows, so does your access — to faster response times, dedicated teams, and exclusive capabilities.
          </p>
        </div>

        {/* Tier cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 xl:gap-6 mb-16">
          {tiers.map((tier, i) => (
            <TierCard key={tier.id} tier={tier} index={i} />
          ))}
        </div>

        {/* Upgrade path visual */}
        <div className="p-6 xl:p-8 bg-studio-900 rounded-2xl border border-studio-700/50">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-0">
            <div className="text-center md:text-left flex-1">
              <p className="text-studio-500 text-xs font-semibold tracking-[0.15em] uppercase mb-2">Upgrade Path</p>
              <h4 className="font-display font-bold text-lg text-white mb-1">How to Rank Up</h4>
              <p className="text-studio-400 text-sm leading-relaxed max-w-xs">
                Tiers are reviewed quarterly. Hit the project or spend threshold and your account manager will reach out.
              </p>
            </div>

            <div className="flex-1 flex items-center justify-center gap-4 md:gap-6">
              {/* Standard */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-xl bg-studio-700/60 border border-studio-600/50 flex items-center justify-center">
                  <Shield size={20} className="text-studio-300" />
                </div>
                <span className="text-studio-300 text-xs font-semibold">Standard</span>
              </div>

              <div className="flex flex-col items-center gap-1">
                <ArrowRight size={16} className="text-gold-500/40" />
                <span className="text-studio-600 text-[10px]">3+ projects</span>
              </div>

              {/* Gold */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-xl bg-gold-500/10 border border-gold-500/30 flex items-center justify-center">
                  <Star size={20} className="text-gold-400" />
                </div>
                <span className="text-gold-400 text-xs font-semibold">Gold</span>
              </div>

              <div className="flex flex-col items-center gap-1">
                <ArrowRight size={16} className="text-white/20" />
                <span className="text-studio-600 text-[10px]">Invitation</span>
              </div>

              {/* VIP */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-xl bg-white/8 border border-white/15 flex items-center justify-center">
                  <Crown size={20} className="text-white" />
                </div>
                <span className="text-white text-xs font-semibold">VIP</span>
              </div>
            </div>

            <div className="flex-1 md:text-right text-center">
              <p className="text-studio-500 text-xs mb-3">Currently 11 Gold clients · 4 VIP accounts</p>
              <button
                onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center gap-2 px-4 py-2.5 gradient-gold text-studio-950 text-sm font-semibold rounded-xl hover:opacity-90 transition-all duration-200 active:scale-95"
              >
                Check Your Eligibility
                <ArrowRight size={13} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}